import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {noop} from '@github-ui/noop'
import {graphql, useFragment} from 'react-relay'

import {QUERY_FIELDS} from '../constants/queries'
import type {CommonItemProps} from '../constants/types'
import type {IssueItem$key} from './__generated__/IssueItem.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import styles from './IssueItem.module.css'
import {IssueItemBlockedBy} from './IssueItemBlockedBy'
import {type IssueItemMetadataProps, LazyIssueItemMetadata} from './IssueItemMetadata'
import {IssueOrPullRequestUnreadIndicator} from './IssueOrPullRequestUnreadIndicator'
import {IssuePullRequestDescription} from './IssuePullRequestDescription'
import {IssuePullRequestStateIcon} from './IssuePullRequestStateIcon'
import {IssuePullRequestTitle} from './IssuePullRequestTitle'
import {IssueTypeIndicator} from './IssueTypeIndicator'

// This component represents a single 'basic' issue displayed in the list
// It styles a given issue using the ListItem component

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueItemQuery = graphql`
  fragment IssueItem on Issue
  @argumentDefinitions(
    labelPageSize: {type: "Int!", defaultValue: 10}
    includeMilestone: {type: "Boolean!", defaultValue: true}
  ) {
    id
    __typename
    titleHtml: titleHTML # alias resolves a type conflict with Issue and IssuePullRequestTitle titleHTML
    issueType {
      id
      name
      color
    }
    ...IssuePullRequestTitle @arguments(labelPageSize: $labelPageSize)
    ...IssuePullRequestDescription @arguments(includeMilestone: $includeMilestone)
    ...IssuePullRequestStateIcon
  }
`

type IssueItemProps = {
  itemKey: IssueItem$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  showRepository?: boolean
  sortingItemSelected?: string
  isActive?: boolean
  getAdditiveMetadataHref: (type: keyof typeof QUERY_FIELDS, name: string) => string
  onFocus?: () => void
  repositoryOwner: string
  repositoryName: string
  as?: React.ElementType
  role?: string
} & Omit<IssueItemMetadataProps, 'itemKey'> &
  CommonItemProps

export const IssueItem = ({
  itemKey,
  listItemMetadata,
  isSelected,
  isActive = false,
  showCommentCount,
  showCommentZeroCount,
  showAssignees = true,
  showRepository = true,
  onSelect = noop,
  onFocus = noop,
  onClick = noop,
  getMetadataHref,
  getAdditiveMetadataHref,
  sortingItemSelected,
  ref,
  href,
  repositoryOwner,
  repositoryName,
  as,
  role,
}: IssueItemProps) => {
  const data = useFragment(IssueItemQuery, itemKey)

  const titleValue = data && data.titleHtml

  const title = (
    <IssuePullRequestTitle
      value={titleValue}
      dataKey={data}
      listItemMetadata={listItemMetadata}
      repositoryOwner={repositoryOwner}
      repositoryName={repositoryName}
      ref={ref}
      href={href}
      onClick={onClick}
      getLabelHref={label => getMetadataHref(QUERY_FIELDS.label, label)}
      getSubIssuesHref={(owner: string, repo: string, number: number) =>
        getMetadataHref(QUERY_FIELDS['parent-issue'], `${owner}/${repo}#${number}`)
      }
    />
  )
  const description = (
    <IssuePullRequestDescription
      repositoryOwner={repositoryOwner}
      repositoryName={repositoryName}
      dataKey={data}
      showRepository={showRepository}
      sortingItemSelected={sortingItemSelected}
      getAuthorHref={(login: string) => getMetadataHref('author', login)}
      id={data.id}
    />
  )

  const lazyMetadata = (
    <LazyIssueItemMetadata
      issueId={data.id}
      listItemMetadata={listItemMetadata}
      getMetadataHref={getMetadataHref}
      showAssignees={showAssignees}
      sortingItemSelected={sortingItemSelected}
      showCommentCount={showCommentCount}
      showCommentZeroCount={showCommentZeroCount}
      showLinkedPullRequests
    />
  )

  return (
    <ListItem
      key={data.id}
      title={title}
      isSelected={isSelected}
      isActive={isActive}
      onSelect={onSelect}
      onFocus={onFocus}
      metadata={lazyMetadata}
      metadataContainerClassName={styles.ListItem_0}
      role={role || 'listitem'}
      as={as}
    >
      <IssueOrPullRequestUnreadIndicator listItemMetadata={listItemMetadata} issueId={data.id} />
      <ListItemLeadingContent className={styles.leadingContent}>
        <IssuePullRequestStateIcon dataKey={data} />
      </ListItemLeadingContent>
      <ListItemMainContent>
        <ListItemDescription>
          {data.issueType && data.issueType.name && (
            <IssueTypeIndicator
              issueTypeName={data.issueType.name}
              issueTypeColor={data.issueType.color}
              getIssueTypeHref={(name: string) => getMetadataHref('type', name)}
            />
          )}
          <IssueItemBlockedBy
            listItemMetadata={listItemMetadata}
            issueId={data.id}
            getIssueTypeHref={() => getAdditiveMetadataHref(QUERY_FIELDS.is, 'blocked')}
          />
          {description}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}
IssueItem.nodeType = 'issue'
